import { createContext, useContext } from "react";

/**
 * PB-4923 AC6 — resolve the person a skipped operation is attributed to.
 *
 * The executor reports a skip with a bare `actorId`. AC6 asks the product to
 * say WHICH PERSON edited which field, and a uuid is not that. This is the seam
 * that turns one into the other.
 *
 * A CONTEXT rather than a prop, and mounted low rather than high, because of
 * where each half lives: `WidgetEntityResolverWiring` sits at the app router,
 * above every workspace, while the only things that know a person's name — the
 * access adapter's roster and the canvas presence list — belong to one
 * workspace. `resolver.useWidget()` runs in the CONSUMER's render, inside that
 * workspace, so a `useContext` here reads a provider mounted below the wiring.
 *
 * It resolves; it never fetches. Both sources are already in memory for the
 * reason they exist, so naming a skip costs no request — which is what makes it
 * safe to do on a toast that may fire during rapid typing.
 */
export type WidgetActorNameResolver = (userId: string) => string | null;

const Ctx = createContext<WidgetActorNameResolver | null>(null);

export const WidgetActorNameProvider = Ctx.Provider;

/**
 * The resolver, or a function that always answers `null`.
 *
 * `null` is a real answer and callers must honour it: a former member, or
 * somebody the roster has not resolved yet, has no name available, and the copy
 * must then say "someone else" rather than a guess or an id fragment.
 */
export function useWidgetActorName(): WidgetActorNameResolver {
  return useContext(Ctx) ?? nobody;
}

const nobody: WidgetActorNameResolver = () => null;
